/**
 * SceneBackdrop — luxury editorial motion for lightened sections.
 *
 * Layers (bottom → top):
 *   1. Champagne radial glow (slow pulse)
 *   2. Giant kinetic typography drifting horizontally (ultra-light cream)
 *   3. Crisp SVG line-art (chamber / rings / molecular) — thin gold, slow rotate
 *   4. Floating gold particles
 *   5. Subtle grid mask veil
 *
 * No blurred photos. Everything is intentional and recognisable.
 *
 * Usage:
 *   <SceneBackdrop variant="cream" label="OXYGEN" art="chamber" />
 */

const PALETTES = {
  cream: { veil: "rgba(250, 249, 246, 0.35)", accent: "rgba(201, 169, 97, 0.16)", type: "rgba(28,28,28,0.045)" },
  sand:  { veil: "rgba(245, 243, 238, 0.32)", accent: "rgba(201, 169, 97, 0.20)", type: "rgba(28,28,28,0.055)" },
};

export default function SceneBackdrop({ variant = "cream", label = "THERATEC", art = "chamber" }) {
  const p = PALETTES[variant] || PALETTES.cream;

  return (
    <div
      aria-hidden="true"
      data-testid={`scene-backdrop-${variant}`}
      className="absolute inset-0 pointer-events-none overflow-hidden"
    >
      {/* 1. Champagne halo — slow pulse */}
      <div
        className="absolute rounded-full"
        style={{
          top: "-20%",
          right: "-15%",
          width: "60vw",
          height: "60vw",
          maxWidth: "900px",
          maxHeight: "900px",
          background: p.accent,
          filter: "blur(120px)",
          animation: "tt-halo-pulse 14s ease-in-out infinite",
        }}
      />
      <div
        className="absolute rounded-full"
        style={{
          bottom: "-25%",
          left: "-10%",
          width: "45vw",
          height: "45vw",
          maxWidth: "700px",
          maxHeight: "700px",
          background: p.accent,
          filter: "blur(140px)",
          animation: "tt-halo-pulse 18s ease-in-out infinite",
          animationDelay: "-6s",
        }}
      />

      {/* 2. Kinetic typography — drifts slowly across */}
      <div
        className="absolute inset-x-0 flex items-center whitespace-nowrap select-none"
        style={{
          top: "50%",
          transform: "translateY(-50%)",
          animation: "tt-type-drift 60s linear infinite",
        }}
      >
        <span
          className="font-serif-editorial italic"
          style={{
            fontSize: "clamp(140px, 24vw, 340px)",
            color: p.type,
            letterSpacing: "-0.03em",
            fontWeight: 400,
            lineHeight: 1,
            paddingRight: "6vw",
          }}
        >
          {label}
        </span>
        <span
          className="font-serif-editorial italic"
          style={{
            fontSize: "clamp(140px, 24vw, 340px)",
            color: p.type,
            letterSpacing: "-0.03em",
            fontWeight: 400,
            lineHeight: 1,
            paddingRight: "6vw",
          }}
        >
          {label}
        </span>
      </div>

      {/* 3. Line-art SVG — crisp gold, slow rotate */}
      <div
        className="absolute"
        style={{
          top: "10%",
          right: "-6%",
          width: "min(48vw, 620px)",
          height: "min(48vw, 620px)",
          animation: "tt-art-drift 40s ease-in-out infinite",
        }}
      >
        <LineArt kind={art} />
      </div>

      {/* Secondary line-art on opposite side, smaller */}
      <div
        className="absolute"
        style={{
          bottom: "6%",
          left: "-4%",
          width: "min(34vw, 420px)",
          height: "min(34vw, 420px)",
          animation: "tt-art-drift 52s ease-in-out infinite reverse",
          animationDelay: "-14s",
          opacity: 0.6,
        }}
      >
        <LineArt kind={art === "chamber" ? "rings" : "chamber"} />
      </div>

      {/* 4. Floating gold particles */}
      {PARTICLES.map((pt, i) => (
        <span
          key={i}
          className="absolute rounded-full"
          style={{
            left: `${pt.x}%`,
            top: `${pt.y}%`,
            width: pt.s,
            height: pt.s,
            background: "#C9A961",
            opacity: pt.o,
            boxShadow: `0 0 ${pt.s * 3}px rgba(201,169,97,0.5)`,
            animation: `tt-particle-float ${pt.d}s ease-in-out infinite`,
            animationDelay: `${pt.delay}s`,
          }}
        />
      ))}

      {/* 5. Veil to keep foreground crisp */}
      <div className="absolute inset-0" style={{ background: p.veil }} />
    </div>
  );
}

/* -------------------------------- Line art -------------------------------- */

const GOLD_A = "#C9A961";
const GOLD_B = "#E8D6A8";

function LineArt({ kind }) {
  if (kind === "rings")     return <RingsArt />;
  if (kind === "molecular") return <MolecularArt />;
  if (kind === "wave")      return <WaveArt />;
  return <ChamberArt />;
}

function ChamberArt() {
  // Thin editorial contour of a hyperbaric chamber (side profile)
  return (
    <svg viewBox="0 0 600 600" className="w-full h-full" fill="none">
      <defs>
        <linearGradient id="sb-gold" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={GOLD_B} stopOpacity="0.9" />
          <stop offset="100%" stopColor={GOLD_A} stopOpacity="0.85" />
        </linearGradient>
      </defs>
      {/* Outer capsule */}
      <path
        d="M 90 300 Q 90 170 220 170 L 400 170 Q 520 170 520 300 Q 520 430 400 430 L 220 430 Q 90 430 90 300 Z"
        stroke="url(#sb-gold)"
        strokeWidth="1.5"
        opacity="0.75"
      />
      {/* Inner capsule */}
      <path
        d="M 120 300 Q 120 200 230 200 L 390 200 Q 490 200 490 300 Q 490 400 390 400 L 230 400 Q 120 400 120 300 Z"
        stroke="url(#sb-gold)"
        strokeWidth="1"
        opacity="0.5"
      />
      {/* Front porthole */}
      <circle cx="180" cy="300" r="55" stroke="url(#sb-gold)" strokeWidth="1.2" opacity="0.7" />
      <circle cx="180" cy="300" r="42" stroke="url(#sb-gold)" strokeWidth="0.8" opacity="0.4" />
      <circle cx="180" cy="300" r="4" fill={GOLD_A} opacity="0.6" />
      {/* Pressure gauge cluster */}
      <circle cx="440" cy="240" r="18" stroke="url(#sb-gold)" strokeWidth="1" opacity="0.55" />
      <circle cx="440" cy="240" r="2" fill={GOLD_A} opacity="0.6" />
      <line x1="440" y1="240" x2="450" y2="232" stroke={GOLD_A} strokeWidth="1" opacity="0.6" />
      {/* Base rail */}
      <line x1="80" y1="470" x2="530" y2="470" stroke="url(#sb-gold)" strokeWidth="0.8" opacity="0.35" />
      <line x1="140" y1="430" x2="140" y2="470" stroke={GOLD_A} strokeWidth="0.8" opacity="0.35" />
      <line x1="470" y1="430" x2="470" y2="470" stroke={GOLD_A} strokeWidth="0.8" opacity="0.35" />
      {/* Corner registration marks */}
      <path d="M 40 40 L 60 40 M 40 40 L 40 60" stroke={GOLD_A} strokeWidth="1" opacity="0.4" />
      <path d="M 560 560 L 540 560 M 560 560 L 560 540" stroke={GOLD_A} strokeWidth="1" opacity="0.4" />
    </svg>
  );
}

function RingsArt() {
  // Concentric wellness rings + radial spokes
  return (
    <svg viewBox="0 0 600 600" className="w-full h-full" fill="none">
      <defs>
        <linearGradient id="sb-ring" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={GOLD_B} stopOpacity="0.9" />
          <stop offset="100%" stopColor={GOLD_A} stopOpacity="0.6" />
        </linearGradient>
      </defs>
      {[280, 230, 180, 130, 80].map((r, i) => (
        <circle
          key={r}
          cx="300"
          cy="300"
          r={r}
          stroke="url(#sb-ring)"
          strokeWidth={i === 0 ? "1.4" : "0.9"}
          opacity={0.85 - i * 0.12}
          strokeDasharray={i === 2 ? "3 8" : "none"}
        />
      ))}
      {/* Radial spokes */}
      {[0, 30, 60, 90, 120, 150].map((deg) => (
        <line
          key={deg}
          x1="300"
          y1="300"
          x2={300 + 300 * Math.cos((deg * Math.PI) / 180)}
          y2={300 + 300 * Math.sin((deg * Math.PI) / 180)}
          stroke={GOLD_A}
          strokeWidth="0.5"
          opacity="0.15"
        />
      ))}
      {/* Center dot */}
      <circle cx="300" cy="300" r="4" fill={GOLD_A} opacity="0.7" />
      {/* Corner marks */}
      <path d="M 20 20 L 50 20 M 20 20 L 20 50" stroke={GOLD_A} strokeWidth="1" opacity="0.4" />
      <path d="M 580 580 L 550 580 M 580 580 L 580 550" stroke={GOLD_A} strokeWidth="1" opacity="0.4" />
    </svg>
  );
}

function MolecularArt() {
  // Connected node network — O2 / O3 style diagram
  const nodes = [
    { x: 300, y: 160, r: 22 },
    { x: 160, y: 280, r: 18 },
    { x: 440, y: 280, r: 18 },
    { x: 220, y: 440, r: 14 },
    { x: 380, y: 440, r: 14 },
    { x: 300, y: 320, r: 10 },
  ];
  const edges = [
    [0, 1], [0, 2], [1, 3], [2, 4], [3, 5], [4, 5], [1, 2], [0, 5],
  ];
  return (
    <svg viewBox="0 0 600 600" className="w-full h-full" fill="none">
      <defs>
        <linearGradient id="sb-mol" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={GOLD_B} stopOpacity="0.9" />
          <stop offset="100%" stopColor={GOLD_A} stopOpacity="0.7" />
        </linearGradient>
      </defs>
      {edges.map(([a, b], i) => (
        <line
          key={i}
          x1={nodes[a].x}
          y1={nodes[a].y}
          x2={nodes[b].x}
          y2={nodes[b].y}
          stroke="url(#sb-mol)"
          strokeWidth="0.9"
          opacity="0.5"
        />
      ))}
      {nodes.map((n, i) => (
        <g key={i}>
          <circle cx={n.x} cy={n.y} r={n.r} stroke="url(#sb-mol)" strokeWidth="1.2" opacity="0.85" />
          <circle cx={n.x} cy={n.y} r={n.r - 5} stroke={GOLD_A} strokeWidth="0.5" opacity="0.4" />
          <circle cx={n.x} cy={n.y} r="2" fill={GOLD_A} opacity="0.6" />
        </g>
      ))}
      <path d="M 20 20 L 50 20 M 20 20 L 20 50" stroke={GOLD_A} strokeWidth="1" opacity="0.4" />
      <path d="M 580 580 L 550 580 M 580 580 L 580 550" stroke={GOLD_A} strokeWidth="1" opacity="0.4" />
    </svg>
  );
}

function WaveArt() {
  // Layered pressure waves
  return (
    <svg viewBox="0 0 600 600" className="w-full h-full" fill="none">
      <defs>
        <linearGradient id="sb-wave" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={GOLD_A} stopOpacity="0.2" />
          <stop offset="50%" stopColor={GOLD_B} stopOpacity="0.9" />
          <stop offset="100%" stopColor={GOLD_A} stopOpacity="0.2" />
        </linearGradient>
      </defs>
      {[0, 60, 120, 180, 240].map((offset, i) => (
        <path
          key={i}
          d={`M 0 ${200 + offset} Q 150 ${140 + offset} 300 ${200 + offset} T 600 ${200 + offset}`}
          stroke="url(#sb-wave)"
          strokeWidth={i === 2 ? "1.4" : "0.9"}
          opacity={0.75 - i * 0.12}
        />
      ))}
      <path d="M 20 20 L 50 20 M 20 20 L 20 50" stroke={GOLD_A} strokeWidth="1" opacity="0.4" />
    </svg>
  );
}

/* -------------------------------- Particles -------------------------------- */

const PARTICLES = [
  { x: 12, y: 22, s: 3, o: 0.55, d: 9,  delay: 0 },
  { x: 28, y: 68, s: 2, o: 0.45, d: 11, delay: -2 },
  { x: 46, y: 18, s: 4, o: 0.65, d: 13, delay: -5 },
  { x: 62, y: 76, s: 2, o: 0.4,  d: 10, delay: -1 },
  { x: 78, y: 34, s: 3, o: 0.55, d: 12, delay: -7 },
  { x: 88, y: 82, s: 2, o: 0.4,  d: 9,  delay: -3 },
  { x: 18, y: 88, s: 3, o: 0.5,  d: 14, delay: -6 },
  { x: 54, y: 44, s: 2, o: 0.4,  d: 11, delay: -8 },
  { x: 72, y: 12, s: 3, o: 0.55, d: 10, delay: -4 },
];
